import React, { useEffect, useRef, useState } from 'react';
import AppHeader from '../components/AppHeader';
import RecommendedStoreCard from '../components/RecommendedStoreCard';
import { useOrder } from '../state/OrderContext';
import { useRecipeLibrary } from '../state/RecipeLibraryContext';
import { useRecipeSearch } from '../hooks/useRecipeSearch';
import { describeMatch } from '../lib/matchRecipes';
import { alternateRoute, resolveRoute, ROUTE_LABELS } from '../lib/classifyInput';
import { createRequestGuard } from '../lib/requestGuard';
import { extractRecipe, isCancellation } from '../mocks/extractRecipe';
import {
  AlertIcon,
  ArrowRightIcon,
  ChevronRightIcon,
  ClipboardIcon,
  ClockIcon,
  CloseIcon,
  ListIcon,
  PaperclipIcon,
  PersonIcon,
  PotIcon,
  RefreshIcon,
  UploadIcon,
  VideoIcon,
} from '../components/icons';
import './Home.css';

// Home — the landing screen, and the one place every input route starts.
//
// Stage 2 made a typed idea search the user's own recipes first. Stage 3 adds
// the other two things people actually do: paste a whole recipe, and hand over
// a link. All three land in the same box, so the first job is working out
// which one this is — and saying so, because the guess is sometimes wrong.

function sourceLabel({ recipe, match }) {
  return describeMatch(match) ?? `From the web · ${recipe.origin?.label ?? 'Online'}`;
}

export default function Home({ onOpenRecipe, onOpenQueue, onOpenHistory, seedToken = 0 }) {
  const { input, recipe, servings, playback, selectRecipe, setPlayback, clearPlayback, update } = useOrder();
  const { saveRecipe, findByUrl } = useRecipeLibrary();
  const search = useRecipeSearch();

  const [draft, setDraft] = useState(input);
  // Null means "trust the classifier". Set only when the user corrects it, and
  // cleared whenever they type something new — a correction applies to what
  // they corrected, not to everything they ever paste afterwards.
  const [override, setOverride] = useState(null);
  const [extractStatus, setExtractStatus] = useState('idle');
  const [extractError, setExtractError] = useState(null);
  // What is actually in flight, which is not always what the box says: picking
  // a video leaves the textarea empty, and the spinner still has to name the
  // right thing.
  const [pending, setPending] = useState(null);

  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const guardRef = useRef(createRequestGuard());
  const abortRef = useRef(null);

  const trimmed = draft.trim();
  const route = resolveRoute(trimmed, override);
  const otherRoute = trimmed ? alternateRoute(route) : null;

  // Adopting a recipe also files it in the library, so it stays reusable next
  // time regardless of where it came from. Library membership is not saving —
  // `savedAt` is only set when the user explicitly saves.
  function adopt(chosen, match) {
    saveRecipe(chosen);
    selectRecipe(chosen, trimmed, match);
    onOpenRecipe();
  }

  // SWAP POINT boundary. Everything that can fail on an input route goes
  // through here, so there is exactly one place that decides what a failure
  // looks like to the user.
  async function runExtraction(request) {
    const token = guardRef.current.begin();
    const controller = new AbortController();
    abortRef.current = controller;

    setExtractError(null);
    setExtractStatus('loading');
    setPending(request.source);

    try {
      const extracted = await extractRecipe({ ...request, signal: controller.signal });
      if (!guardRef.current.isCurrent(token)) return; // a newer request won
      setExtractStatus('idle');
      setPending(null);
      adopt(extracted, null);
    } catch (err) {
      if (!guardRef.current.isCurrent(token)) return;
      if (isCancellation(err)) {
        setExtractStatus('idle');
        return;
      }
      setExtractError({
        message: err?.message || "We couldn't read that.",
        reason: err?.reason ?? 'unreadable',
        // Retry is offered only where trying again could plausibly change the
        // answer. A private or unsupported link will fail identically forever.
        retryable: Boolean(err?.retryable),
      });
      setExtractStatus('error');
    }
  }

  function cancelExtraction() {
    guardRef.current.cancel();
    abortRef.current?.abort();
    setExtractStatus('idle');
    setPending(null);
  }

  function resolve() {
    if (!trimmed) return;

    if (route === 'url') {
      // A link we already hold is not worth re-reading, and re-reading it
      // would lose whatever the user has since done with it.
      const known = findByUrl(trimmed);
      if (known) {
        adopt(known, { kind: known.savedAt ? 'saved' : 'library', savedAt: known.savedAt, lastCookedAt: null });
        return;
      }
      runExtraction({ source: 'link', value: trimmed });
      return;
    }

    if (route === 'recipeText') {
      runExtraction({ source: 'text', value: draft });
      return;
    }

    search.search(trimmed);
  }

  // Queue, History and anything shared into the app all hand a meal back the
  // same way. This goes through the full route resolution rather than straight
  // to search, because a queued video link or a shared link has to be
  // extracted, not searched for by name.
  useEffect(() => {
    if (!seedToken || !input.trim()) return;
    setDraft(input);
    setOverride(null);
    const seeded = resolveRoute(input.trim(), null);
    if (seeded === 'url') {
      const known = findByUrl(input.trim());
      if (known) {
        saveRecipe(known);
        selectRecipe(known, input.trim(), {
          kind: known.savedAt ? 'saved' : 'library',
          savedAt: known.savedAt,
          lastCookedAt: null,
        });
        onOpenRecipe();
        return;
      }
      runExtraction({ source: 'link', value: input.trim() });
      return;
    }
    if (seeded === 'recipeText') {
      runExtraction({ source: 'text', value: input });
      return;
    }
    search.search(input);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seedToken]);

  function handleInputChange(event) {
    setDraft(event.target.value);
    // The override deliberately survives typing. It has to: the fallback after
    // a failed link is "paste the recipe text", and that mode would evaporate
    // the moment the user started pasting. Clearing the box clears it instead,
    // and a link always overrules it anyway — see resolveRoute.
    if (extractStatus === 'error') {
      setExtractStatus('idle');
      setExtractError(null);
    }
    search.reset();
  }

  function clearInput() {
    setDraft('');
    setOverride(null);
    setExtractStatus('idle');
    setExtractError(null);
    search.reset();
    update({ input: '' });
  }

  // Picking a video does two separate things, and keeping them separate is the
  // point: the file becomes something the user can watch, and extraction is
  // attempted and honestly fails. Playback is not evidence that we read it.
  function handleVideoPick(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setPlayback(file);
    search.reset();
    runExtraction({ source: 'video-file', fileName: file.name });
  }

  // The fallback offered when a link or a video can't be read: write it out by
  // hand. The video stays attached so it can be watched while typing.
  function switchToPaste() {
    setDraft('');
    setOverride('recipeText');
    setExtractStatus('idle');
    setExtractError(null);
    requestAnimationFrame(() => textareaRef.current?.focus());
  }

  const isSearching = search.status === 'loading' || extractStatus === 'loading';
  const isFindingAnother = search.status === 'loadingMore';
  const result = search.status === 'ready' ? search.current : null;

  const busyLabel =
    pending === 'link'
      ? 'Reading that link…'
      : pending === 'text'
        ? 'Reading your recipe…'
        : pending === 'video-file'
          ? 'Looking at your video…'
          : 'Checking your recipes…';

  return (
    <div className="screen">
      <div className="screen__inner">
        <AppHeader />
        <div className="home-nav">
          <button type="button" className="home-nav__link" onClick={onOpenQueue}>
            <ListIcon size={14} /> Queue
          </button>
          <button type="button" className="home-nav__link" onClick={onOpenHistory}>
            <ClockIcon size={14} /> History
          </button>
        </div>

        <h1 className="screen__title">What are you making?</h1>

        {/* The recipe already chosen stays visible here, so Home and Recipe
            never disagree about which meal is in play. */}
        {recipe && (
          <button type="button" className="card current-recipe" onClick={onOpenRecipe}>
            <span className="current-recipe__icon">
              <PotIcon size={18} />
            </span>
            <span className="current-recipe__body">
              <span className="current-recipe__label">Currently making</span>
              <span className="current-recipe__name">{recipe.dishName}</span>
              <span className="current-recipe__meta">
                {recipe.totalMinutes ? (
                  <>
                    <ClockIcon size={13} /> {recipe.totalMinutes} min
                  </>
                ) : null}
                <PersonIcon size={13} /> {servings} servings
              </span>
            </span>
            <ChevronRightIcon />
          </button>
        )}

        <section className="card">
          <div className="home-input">
            <textarea
              ref={textareaRef}
              className="home-input__field"
              placeholder="Describe a meal, paste a recipe, or paste a link…"
              value={draft}
              onChange={handleInputChange}
              rows={3}
            />
            {draft && (
              <button type="button" className="home-input__clear" onClick={clearInput} aria-label="Clear">
                <CloseIcon size={14} />
              </button>
            )}
            <button
              type="button"
              className="home-input__attach"
              onClick={() => fileInputRef.current?.click()}
              aria-label="Choose a video from your device"
              title="Choose a video from your device"
            >
              <PaperclipIcon size={17} />
            </button>
            <input ref={fileInputRef} type="file" accept="video/*" hidden onChange={handleVideoPick} />
          </div>

          {/* The classifier's guess, out loud and correctable. It is only ever
              a guess, and the user is the one who knows what they pasted. */}
          {trimmed && !isSearching && !result && (
            <p className="route-note">
              Reading this as {ROUTE_LABELS[route]}.
              {otherRoute && (
                <button type="button" className="route-note__switch" onClick={() => setOverride(otherRoute)}>
                  Treat as {ROUTE_LABELS[otherRoute]} instead
                </button>
              )}
            </p>
          )}

          {playback && (
            <p className="playback-note">
              <VideoIcon size={15} /> {playback.name}
              <button type="button" className="playback-note__clear" onClick={clearPlayback}>
                Remove
              </button>
            </p>
          )}

          {isSearching ? (
            <p className="status home-status">
              <span className="spinner" />
              {busyLabel}
              {extractStatus === 'loading' && (
                <button type="button" className="retry-button" onClick={cancelExtraction}>
                  Cancel
                </button>
              )}
            </p>
          ) : (
            !result && (
              <button
                type="button"
                className="button button--primary home-parse-button"
                disabled={!trimmed}
                onClick={resolve}
              >
                Get the recipe <ArrowRightIcon size={18} />
              </button>
            )
          )}

          {/* Failure, with the ways out that actually apply. Retry appears only
              for failures that could go differently next time. */}
          {extractStatus === 'error' && extractError && (
            <div className="status status--error home-status extract-error">
              <p className="extract-error__message">
                <AlertIcon size={16} /> {extractError.message}
              </p>
              <div className="extract-error__actions">
                {extractError.retryable && (
                  <button type="button" className="retry-button" onClick={resolve}>
                    Try again
                  </button>
                )}
                <button type="button" className="fallback-button" onClick={switchToPaste}>
                  <ClipboardIcon size={15} /> Paste the recipe text
                </button>
                {extractError.reason !== 'unsupported-media' && (
                  <button
                    type="button"
                    className="fallback-button"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <UploadIcon size={15} /> Choose the video instead
                  </button>
                )}
              </div>
            </div>
          )}

          {search.status === 'error' && (
            <p className="status status--error home-status">
              <AlertIcon size={16} /> {search.error}
              <button type="button" className="retry-button" onClick={search.retry}>
                Try again
              </button>
            </p>
          )}

          {/* Deliberately does not tell the user to phrase it better. After the
              conflict veto in matchRecipes, "chicken pad thai" can find nothing
              while being a perfectly clear request — the gap is ours, not
              theirs, and the useful next step is their own recipe. */}
          {search.status === 'empty' && (
            <p className="status home-status">
              <AlertIcon size={16} /> We couldn&rsquo;t find that one. You can paste the recipe or a
              link to it instead.
            </p>
          )}
        </section>

        {/* Second card, directly under the box — but only while the box is the
            thing on screen. Once a recipe result arrives, that result is what
            the user asked for and it belongs against the input, not pushed a
            card down by a shop they can choose at any time. The card renders
            itself away when no recommended branch is known. */}
        {!result && !isSearching && <RecommendedStoreCard />}

        {/* One recommendation, never a list. Name, servings, ingredient count,
            total time and where it came from all read together. */}
        {result && (
          <section className="card result">
            <p className="result__name">{result.recipe.dishName}</p>
            <p className="result__facts">
              {result.recipe.servings} servings · {result.recipe.ingredients.length} ingredients ·{' '}
              {result.recipe.totalMinutes} min total
            </p>
            <p className="result__source">{sourceLabel(result)}</p>

            <button
              type="button"
              className="button button--primary result__use"
              onClick={() => adopt(result.recipe, result.match)}
            >
              Use this recipe <ArrowRightIcon size={18} />
            </button>

            {search.hasAlternatives && (
              <button
                type="button"
                className="result__another"
                onClick={search.tryAnother}
                disabled={isFindingAnother}
              >
                {isFindingAnother ? (
                  <>
                    <span className="spinner" /> Looking for another…
                  </>
                ) : (
                  <>
                    <RefreshIcon size={15} /> Try another
                  </>
                )}
              </button>
            )}

            {/* The one outcome the card alone cannot show: we searched past the
                local matches and there was nothing else. Saying it is the
                difference between "no more options" and "the button is broken". */}
            {search.exhausted && (
              <p className="result__exhausted">
                That&rsquo;s everything we found for &ldquo;{search.query}&rdquo;. You can paste a
                recipe or a link if you had something else in mind.
              </p>
            )}
          </section>
        )}
      </div>
    </div>
  );
}
